import { useState, type JSX, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApp, type Screen } from '../state/app.js';
import { expenseApi } from '../api/expense.js';
import { EXPIRY_WARN_DAYS, daysUntil } from '@dxtech/shared';
import { fetchAssets } from '../screens/Assets.js';
import { isEmbedded } from '../auth/authService.js';
import { APP_VERSION, BUILD_TIME_LABEL } from '../buildInfo.js';
import { Avatar } from '../ui/common.js';
import { MyProfileModal } from '../modals/MyProfileModal.js';
import {
  IconAdmin, IconApprove, IconBoard, IconCI, IconCompany, IconHome, IconLeave, IconLibrary, IconPicker, IconResources, IconSettings, IconStats, IconPeopleStats, IconTrip, IconWeekly, IconExpense, IconOvertime, IconKudos, IconAssets, IconMeals,
} from '../ui/icons.js';

function NavItem({ active, onClick, icon, label, badge, sub }: {
  active: boolean; onClick: () => void; icon: ReactNode; label: string; badge?: number; sub?: boolean;
}): JSX.Element {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 11, width: '100%', border: 'none', textAlign: 'left',
      fontFamily: 'inherit', fontSize: sub ? 13 : 13.5, fontWeight: sub && !active ? 500 : 600, padding: sub ? '7px 10px' : '9px 11px', borderRadius: 8, cursor: 'pointer',
      background: active ? '#EFF4FE' : 'transparent', color: active ? '#1D4ED8' : '#4B5563',
    }}>
      {icon}{label}
      {badge ? (
        <span style={{
          marginLeft: 'auto', background: '#EF4444', color: '#fff', fontSize: 11, fontWeight: 700,
          minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>{badge}</span>
      ) : null}
    </button>
  );
}

export function Sidebar({ pendingCount = 0 }: { pendingCount?: number }): JSX.Element {
  const { me, screen, setScreen, roleLabel } = useApp();
  const p = me.permissions;
  const go = (s: Screen) => () => setScreen(s);
  const [profileOpen, setProfileOpen] = useState(false);
  // CEO: 결재·지급할 경비 건수 배지
  const isCeo = me.roles.includes('CEO');
  const expenseAll = useQuery({ queryKey: ['expenses', 'all'], queryFn: () => expenseApi.list('all'), enabled: isCeo, refetchInterval: 120_000 });
  const expenseBadge = isCeo ? (expenseAll.data?.items ?? []).filter((e) => e.status === 'submitted' || e.status === 'approved').length : 0;

  // 관리자: 30일 안에 만료(또는 만료된) 자산 건수 배지
  const isAssetAdmin = isCeo || me.roles.includes('Admin');
  const assets = useQuery({ queryKey: ['assets'], queryFn: fetchAssets, enabled: isAssetAdmin, refetchInterval: 600_000 });
  const kst = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
  const assetBadge = (assets.data?.items ?? []).filter((a) => { const d = daysUntil(a.expiresAt, kst); return d != null && d <= EXPIRY_WARN_DAYS; }).length;

  // 2단계 메뉴 — 그룹 머리글을 눌러 접고 펼침(현재 화면이 속한 그룹은 항상 펼침). 접힘 상태는 이 브라우저에만 기억
  const groups: Array<{ key: string; label: string; icon: ReactNode; items: Array<{ screen: Screen; label: string; icon: ReactNode; badge?: number; show?: boolean }> }> = [
    { key: 'work', label: '근무', icon: <IconAdmin />, items: [
      // 근무현황 — 전 임직원(상태·일정), 출퇴근 시각은 CEO·임원·관리자만
      { screen: 'admin', label: '근무현황', icon: <IconAdmin /> },
      { screen: 'overtime', label: '야근', icon: <IconOvertime /> },
      { screen: 'leave', label: '휴가', icon: <IconLeave /> },
      { screen: 'trip', label: '출장', icon: <IconTrip /> },
      { screen: 'meals', label: '오늘의 식단', icon: <IconMeals /> },
      { screen: 'approve', label: '휴가 승인', icon: <IconApprove />, badge: pendingCount, show: p.canApproveLeave },
    ] },
    { key: 'task', label: '업무', icon: <IconWeekly />, items: [
      { screen: 'weekly', label: '주간보고', icon: <IconWeekly /> },
      { screen: 'expense', label: '경비지출', icon: <IconExpense />, badge: expenseBadge },
      { screen: 'picker', label: '랜덤피커', icon: <IconPicker /> },
    ] },
    { key: 'company', label: '회사·자료', icon: <IconCompany />, items: [
      { screen: 'company', label: '회사 정보', icon: <IconCompany /> },
      { screen: 'ci', label: 'CI', icon: <IconCI /> },
      { screen: 'resources', label: '문서함', icon: <IconResources /> },
      { screen: 'library', label: '자료실', icon: <IconLibrary /> },
      { screen: 'assets', label: '장비·자산 관리', icon: <IconAssets />, badge: assetBadge },
    ] },
    { key: 'people', label: '임직원', icon: <IconBoard />, items: [
      { screen: 'board', label: '임직원 보드', icon: <IconBoard /> },
      { screen: 'stats', label: '근무통계분석', icon: <IconStats />, show: p.canViewAdmin },
      { screen: 'peopleStats', label: '임직원통계분석', icon: <IconPeopleStats /> },
      { screen: 'kudos', label: '칭찬·감사 카드', icon: <IconKudos /> },
    ] },
  ];
  const [closed, setClosed] = useState<string[]>(() => { try { return JSON.parse(localStorage.getItem('dx-nav-closed') ?? '[]') as string[]; } catch { return []; } });
  const toggle = (key: string) => setClosed((c) => {
    const next = c.includes(key) ? c.filter((k) => k !== key) : [...c, key];
    try { localStorage.setItem('dx-nav-closed', JSON.stringify(next)); } catch { /* 저장 불가 환경은 이번 세션만 */ }
    return next;
  });

  // Teams/Outlook 임베드 시 호스트가 이미 앱 이름을 표시하므로 사이드바 로고는 중복 → 숨김
  const embedded = isEmbedded();

  return (
    <div style={{ width: 236, flexShrink: 0, background: '#fff', borderRight: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', padding: '16px 12px', height: '100%' }}>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px 18px' }}>
          <img src="/brand/DXTECH_MARK.svg" alt="DXTECH" style={{ width: 28, height: 28 }} />
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-.01em' }}>DXTECH Works</div>
        </div>
      )}
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: embedded ? 4 : 0, overflowY: 'auto', minHeight: 0 }}>
        <NavItem active={screen === 'home'} onClick={go('home')} icon={<IconHome />} label="홈" />
        {groups.map((g) => {
          const items = g.items.filter((it) => it.show !== false);
          if (!items.length) return null;
          const hasActive = items.some((it) => it.screen === screen);
          const isOpen = hasActive || !closed.includes(g.key);
          const badge = items.reduce((n, it) => n + (it.badge ?? 0), 0);
          return (
            <div key={g.key}>
              <button onClick={() => toggle(g.key)} aria-expanded={isOpen} style={{
                display: 'flex', alignItems: 'center', gap: 11, width: '100%', border: 'none', background: 'transparent', textAlign: 'left',
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600, padding: '9px 11px', borderRadius: 8, cursor: 'pointer', color: hasActive ? '#1D4ED8' : '#4B5563',
              }}>
                {g.icon}{g.label}
                {!isOpen && badge > 0 && <span style={{ background: '#EF4444', color: '#fff', fontSize: 11, fontWeight: 700, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{badge}</span>}
                <span aria-hidden style={{ marginLeft: 'auto', fontSize: 10, color: '#9CA3AF', transition: 'transform .15s', transform: isOpen ? 'rotate(90deg)' : 'none' }}>▶</span>
              </button>
              {isOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, margin: '1px 0 4px 20px', paddingLeft: 8, borderLeft: '1px solid #EDEFF2' }}>
                  {items.map((it) => <NavItem key={it.screen} sub active={screen === it.screen} onClick={go(it.screen)} icon={it.icon} label={it.label} badge={it.badge} />)}
                </div>
              )}
            </div>
          );
        })}
        {p.canManageConfig && <NavItem active={screen === 'settings'} onClick={go('settings')} icon={<IconSettings />} label="관리자 설정" />}
      </nav>
      {/* 내 정보 — 이름·직책 영역 전체가 버튼(생일·주소·연락처 수정) */}
      <div style={{ marginTop: 'auto', borderTop: '1px solid #F3F4F6', paddingTop: 8 }}>
        <button className="dx-me-btn" onClick={() => setProfileOpen(true)} title="내 정보 수정 (생일·주소·연락처)">
          <Avatar name={me.name} upn={me.upn} size={34} />
          <span style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{me.name}</span>
            <span style={{ display: 'block', fontSize: 11, color: '#9CA3AF' }}>{roleLabel}{me.dept ? ` · ${me.dept}` : ''}</span>
          </span>
          <span className="dx-me-btn-tag">내 정보 ›</span>
        </button>
      </div>
      {profileOpen && <MyProfileModal onClose={() => setProfileOpen(false)} />}
      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #F3F4F6', textAlign: 'center', lineHeight: 1.5 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748B' }}>DXTECH Works v{APP_VERSION}</div>
        <div style={{ fontSize: 10.5, color: '#9CA3AF' }}>Build {BUILD_TIME_LABEL}</div>
      </div>
    </div>
  );
}
